import React, {useMemo, useState, useEffect, useContext, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ReactMoment from 'react-moment';
import Moment from 'moment';
import {
	ConfidentialityModal,
	DocumentName,
	SignoffRequirementsModal,
	ConfirmStartMultiuserEditing
} from '@ey/canvascoreservices';
import {toggleFrameModal, useFrameService} from '@ey/canvascoreservices';
import Env from '../../../util/env';
import {
	useAppState,
	useEngagement,
	useEngagementId,
	useErrors,
	useUserPreferences,
	useUserPresence,
	useEngagementMetadata,
	useConfigurations
} from '../../../util/customHooks';
import {
	dateTimeFormat,
	DocumentHubStatus,
	documentTypes,
	actionTags,
	CanvasFormOpenType,
	Entity,
	documentSearchTypes,
	clientSideEventNames,
	fileExtensionsEligibleForDaaS,
	daasOptions,
	ToastMessageVariants,
	permissionsPolicy
} from '../../../util/uiconstants';
import Utility from '../../../util/utilityfunctions';
import {currentResource, labels} from '../../../util/utils';
import {useAresUrlParameters} from '../../../util/Ares/customHooks';
import DeleteDocument from './DeleteDocument';
import {
	useActions,
	useDocumentUploadStatus,
	useConfidentialityConfiguration,
	useRenameDocumentErrors
} from './DocumentName.utils';
import RenameDocument from './RenameDocument';
import RelateEntities from '../../Main/Ares/Entities/RelateEntities';
import RelateTaskContainer from '../../Main/Ares/Tasks/RelateTask/RelateTaskContainer';
import {SectionIdContext} from '../CanvasForm/Common/Context';
import {raiseInformationMessage} from '../../../actions/erroractions';
import {getDocumentByDocumentId} from '../../../actions/Ares/taskDocumentActions';
import {
	daasCheckInCheckOutDocument,
	getRelatedDocument
} from '../../../actions/FormBodyRelatedDocuments/formbodyrelateddocumentsactions';
import {setFormV2ToasterVisibility} from '../../../actions/toastactions';
import {getEntityDocuments} from '../../../actions/Document/entityDocumentActions';
import {getEngagementIsMest} from '../../../selectors/Ares/selectors';

export default function DocName(props) {
	const {
		currentlyBeingModifiedByLabel = labels.currentlyBeingModifiedBy,
		confidentialityNotOpenableTooltipPlacement,
		confidentialityNotOpenableTooltipContentClassName,
		dateFormat,
		documentEditUserCreateDate,
		documentEditUserName,
		getRelatedDocuments,
		ellipse,
		ellipseMaxLines,
		enableOpenDocument = false,
		inCountryServerBaseUrl = Env.getURL('eng_web'),
		herculesBaseUrl = Env.getURL('hercules_web'),
		lastModifiedFullName,
		lastModifiedByLabel = labels.lastModifiedBy,
		fileSizeLabel = labels.fileSize,
		modifiedDate,
		openedLabelText = labels.openedLabelText,
		shareStatusOwnedLabel = labels.shareStatusOwned,
		shareStatusSharedLabel = labels.shareStatusShared,
		unlinkInProgressLabel = labels.unlinkInProgressLabel,
		unlinkErrorLabel = labels.unlinkError,
		showCurrentUserInfo,
		showLastModifiedUserInfo,
		size,
		useDocHelper = true,
		onDocumentDeleted,
		onDocumentRenamed,
		referrer,
		menuOptions,
		showMenuOptions,
		viewKey,
		entityUId,
		parentName,
		styleName,
		actionTag,
		onDocumentClick,
		onGetDocumentUserPresence,
		onDocumentUserPresenceChanged,
		isLink,
		instructionId = null,
		isDisabled,

		/*the following props are part of the document object*/
		documentId,
		documentName,
		documentStatusId,
		documentTypeId,
		contentContractId,
		contentUri,
		confidentialityTypeId,
		documentChangeTypeId,
		entities,
		fileExt,
		fileSizeKB,
		hasComments,
		hasHiddenSheets,
		hasHighlights,
		isConflicted,
		isShared,
		isLoading,
		isReturned,
		isReadOnly,
		showShared,
		trackChange,
		progressLabel = labels.saving,
		showConfidential,
		documentTasks,
		removeDocumentFromBody,
		isEPSignoffRequired,
		isEPSignoffUserRequired,
		isEQRSignoffRequired,
		isEQRSignoffUserRequired,
		smartEvidenceSourceId,
		smartEvidenceSourceEntityId,
		hasAnySystemAssociated,
		showPriorPeriodMenuOptions,
		showTemporaryMenuOptions,
		showExternalDocumentMenuOptions,
		onAddToEvidence,
		disabled = false,
		isDragAndDropDoc = false,
		filteredEntityType,
		deleteDocumentAction,
		canvasFormOpenType,
		documentNameControls,
		hasRelateTask = false,
		tooltipPosition,
		isFromSampleItemList = false,
		isModalOpen = false,
		accountSTEntityId = 0,
		groupInstructionDrawer = false
	} = props;

	const [showDeleteModal, setShowDeleteModal] = useState(false),
		[showRenameModal, setShowRenameModal] = useState(false),
		[isInProgress, setIsInProgress] = useState(false),
		[showRelateTaskModal, setShowRelateTaskModal] = useState(false),
		[showConfidentialityModal, setShowConfidentialityModal] = useState(false),
		[showHistoryModal, setShowHistoryModal] = useState(false),
		[showSignOffRequirementsModal, setShowSignOffRequirementsModal] = useState(false),
		[showRelateEntitiesModal, setShowRelateEntitiesModal] = useState(false),
		[showModal, setShowModal] = useState(false),
		[daasAction, setDaasAction] = useState({
			checkOut: false,
			checkIn: false
		});

	const aresUrlParameters = useAresUrlParameters();

	const frameService = useFrameService();

	const dispatch = useDispatch();

	const contextSectionId = useContext(SectionIdContext);

	const featureConfigurations = useSelector((store) => store.configurations);
	const allEntities = useSelector((state) => state.stEntities);
	const isAresView = useSelector((state) => state.isAresView);
	const engagementIsMest = useSelector(getEngagementIsMest);

	const userPreferences = useUserPreferences();
	const engagementId = useEngagementId();
	const engagement = useEngagement();
	const {datacenterid} = useEngagementMetadata(engagementId);
	const {features} = useConfigurations();
	const appOnlineState = useAppState();
	const documentUserPresence = useUserPresence(documentId);

	const errors = useErrors();

	const sectionId = useMemo(() => contextSectionId || aresUrlParameters.sectionId, [
		contextSectionId,
		aresUrlParameters
	]);

	const {progressPercent, inProgress} = useDocumentUploadStatus(
		enableOpenDocument,
		documentId,
		documentName,
		fileExt,
		isDragAndDropDoc
	);

	const actions = useActions();
	const confidentialityConfiguration = useConfidentialityConfiguration();
	const renameDocumentErrors = useRenameDocumentErrors();

	const daasDocumentCheckoutCheckinInProgress = Utility.isDaaSDocumentCheckOutCheckInInProgress({
		documentStatusId,
		contentContractId
	});

	const daasDocumentCheckoutComplete = Utility.isDaaSDocumentCheckOutComplete({documentStatusId, contentContractId});
	const daasDocumentCheckinError = Utility.isDaaSDocumentCheckInError({documentStatusId, contentContractId});

	let priorPeriodMenuOptions = [
		{
			name: 'Open read-only',
			label: labels.openReadOnly,
			onClick: (document) => {
				openDocument(
					undefined,
					document,
					actions.downloadDocumentWithDocumentHelper,
					actions.raiseDochelperError,
					'openr'
				);
			}
		}
	];

	let menuOptionsMerged = [
		{
			name: 'Open',
			label: labels.open,
			onClick: () => {
				onOpenClick();
			},
			isDisabled: isDisabled
		},
		{
			name: 'Open read-only',
			label: labels.openReadOnly,
			onClick: (document) => {
				openDocument(
					undefined,
					document,
					actions.downloadDocumentWithDocumentHelper,
					actions.raiseDochelperError,
					'openr'
				);
			},
			isVisible: (document) => {
				return isOpenReadOnlyVisible(document);
			}
		},
		{
			name: 'Start Co-edit mode',
			label: labels.startCoeditMode,
			onClick: () => startMultiuserEditing(),
			isVisible: () => {
				return (
					featureConfigurations &&
					featureConfigurations?.features &&
					featureConfigurations?.features?.enableMultiUserDocumentationCollaboration === true &&
					fileExtensionsEligibleForDaaS.includes(fileExt.toLowerCase()) &&
					!isShared &&
					!isConflicted
				);
			},
			isDisabled: isDisabled
		},
		{
			name: 'End Co-edit mode',
			label: labels.endCoeditMode,
			onClick: () => {
				setDaasAction({checkOut: false, checkIn: true});
				setShowModal(true);
			},
			isVisible: () => {
				return (
					featureConfigurations &&
					featureConfigurations?.features &&
					featureConfigurations?.features?.enableMultiUserDocumentationCollaboration === true &&
					fileExtensionsEligibleForDaaS.includes(fileExt.toLowerCase()) &&
					!isShared
				);
			},
			isDisabled: isDisabled
		},
		{
			name: 'view history',
			label: labels.viewHistory,
			onClick: () => {
				setShowHistoryModal(true);
			}
		},

		{
			name: 'CopyLink',
			label: labels.copyLink,
			onClick: () => {
				const newUrl = Env.getDocumentUrl(
					documentId,
					engagement.engagementId,
					Utility.getParentNameForSelectedTab(parentName)
				);
				newUrl && navigator.clipboard.writeText(newUrl);
			}
		},
		{
			name: 'Rename',
			label: labels.rename,
			isDisabled: isDisabled
		},
		{
			name: 'RelateTasks',
			label: labels.relateTasksTitle,
			onClick: () => setShowRelateTaskModal(true),
			isDisabled: isDisabled
		},
		{
			name: 'RelateEntities',
			label: labels.relateEntitiesTitle,
			onClick: () => {
				return dispatch(getEntityDocuments(engagementId, documentTypeId)).then(() => setShowRelateEntitiesModal(true));
			},
			isVisible: (document) => {
				if (!engagementIsMest) {
					return false;
				}
				if (
					document.documentTypeId === documentTypes.GROUPCOMPONENTINSTRUCTIONDOCUMENT ||
					document.documentTypeId === documentTypes.ESTIMATESDOCUMENT
				) {
					return false;
				}
				if (document.smartEvidenceSourceEntityId || document.hasAnySystemAssociated === true) {
					return false;
				} else {
					return true;
				}
			},
			isDisabled: isDisabled
		},
		{
			name: 'SignoffRequirements',
			label: labels.signOffRequirements,
			onClick: () => {
				setShowSignOffRequirementsModal(true);
			},
			className: (document) => {
				return isDisabled || Utility.isPaperProfile(document.documentTypeId) ? 'isDisabled' : '';
			},
			isDisabled: isDisabled
		},
		{
			name: 'Confidentiality',
			label: labels.confidentiality,
			onClick: () => {
				setShowConfidentialityModal(true);
			},
			isVisible: (document) => {
				return (
					document.engagementId === engagement.engagementId &&
					(document.documentTypeId === documentTypes.EVIDENCE ||
						document.documentTypeId === documentTypes.CRASH ||
						document.documentTypeId === documentTypes.PRIORYEAREVIDENCE ||
						document.documentTypeId === documentTypes.CLIENTFILES)
				);
			},
			isDisabled: isDisabled
		},
		{
			name: 'Unlink',
			label: labels.unlinkLabel,
			onClick: (document) => {
				actions.unlinkDocument(engagementId, document.id, entityUId, viewKey).then(() => {
					dispatch(raiseInformationMessage(labels.unlinkInProgressInfo));
				});
			},
			isVisible: (document) => {
				return document.isShared;
			},
			isDisabled: isDisabled
		},
		{
			name: 'Delete',
			label: labels.delete,
			onClick: () => {
				setShowDeleteModal(true);
			},
			isDisabled: isDisabled
		}
	];

	if (!hasRelateTask) {
		menuOptionsMerged.splice(
			menuOptionsMerged.findIndex((x) => x.name === 'RelateTasks'),
			1
		);
	}

	if (menuOptions !== undefined) {
		if (!showPriorPeriodMenuOptions) {
			const customOptions = menuOptionsMerged.filter((defaultOption) => {
				return menuOptions.some((option) => defaultOption.name === option.name);
			});
			let extra = menuOptions.filter((option) => option.label);
			menuOptionsMerged = customOptions.concat(extra);
		} else {
			menuOptionsMerged = priorPeriodMenuOptions;
		}
	}
	if (
		!isDisabled &&
		removeDocumentFromBody &&
		!showPriorPeriodMenuOptions &&
		filteredEntityType !== documentSearchTypes.gaInstruction
	) {
		menuOptionsMerged.push({
			name: 'RemoveFromBody',
			label: labels.removeFromBody,
			isDisabled: isDisabled,
			onClick: () => {
				removeDocFromBody();
			}
		});
	}

	// This is to avoid unnecessary re-renders because of reference comparison.
	const documentObject = {
		id: documentId,
		documentName,
		documentStatusId,
		documentTypeId,
		documentChangeTypeId,
		confidentialityTypeId,
		engagementId: props.engagementId || engagementId,
		fileExt,
		fileSizeKB,
		entities,
		hasComments,
		hasHiddenSheets,
		hasHighlights,
		isConflicted,
		isReturned,
		isReadOnly,
		isShared,
		size,
		showShared,
		trackChange,
		lastModifiedFullName,
		modifiedDate: modifiedDate !== null ? modifiedDate : new Date().toISOString(), // if modifiedDate is null, set it to current date because it is a new file
		documentTasks,
		documentEditUserCreateDate,
		documentEditUserName,
		isEPSignoffRequired,
		isEPSignoffUserRequired,
		isEQRSignoffRequired,
		isEQRSignoffUserRequired,
		smartEvidenceSourceId,
		smartEvidenceSourceEntityId,
		hasAnySystemAssociated
	};

	const forwardUrl = Utility.getCanvasWebUrlForDocument(
		Utility.getCurrentUrl(window.location),
		documentObject,
		engagement,
		inCountryServerBaseUrl,
		herculesBaseUrl,
		{
			datacenterid: datacenterid,
			features: features,
			stentityid: accountSTEntityId > 0 ? accountSTEntityId : undefined
		}
	);

	const isDocumentEligibleToOpenInV1 = useMemo(
		() => Utility.isDocumentEligibleToOpenInV1(documentObject.documentTypeId),
		[documentObject.documentTypeId]
	);

	const removeDocFromBody = () => {
		if (removeDocumentFromBody) {
			setIsInProgress(true);
			removeDocumentFromBody(documentObject.id, documentObject).finally(() => {
				setIsInProgress(false);
			});
		}
	};

	const isOpenReadOnlyVisible = (documentRef) => {
		return !(
			documentRef.fileExt &&
			documentRef.fileExt.toLowerCase() === 'smt' &&
			documentRef.documentTypeId === documentTypes.PRIORYEAREVIDENCE
		);
	};

	const onOpenClick = () => {
		if (isDocumentEligibleToOpenInV1 || daasDocumentCheckoutComplete || daasDocumentCheckinError) {
			isDocumentEligibleToOpenInV1 ? window.open(forwardUrl, '_blank') : openDaaSDocument(contentUri);
		} else {
			return actions.getUserPresence(engagement.engagementId, documentObject.id, Entity.Document);
		}
	};

	const openDocument = (
		parentName,
		document,
		downloadDocumentWithDocumentHelper,
		raiseDochelperError = undefined,
		actionTag = actionTags.openReadOnly
	) => {
		if (daasDocumentCheckoutCheckinInProgress) return;

		const payload = {
			payloadData: {
				documentHubStatus: DocumentHubStatus.DOWNLOADSTART,
				parentName: parentName,
				documentId: document.id,
				documentChangeTypeId: document.documentChangeTypeId,
				documentName: `${document.documentName}.${document.fileExt}`,
				docTypeId: document.documentTypeId,
				transactionId: document.id,
				documentType: document.documentTypeId,
				ProgressPercent: 0,
				totalPercentage: 100,
				inProgress: true,
				engagementId: document.engagementId,
				dockey: `_${document.id}`
			},
			actions: {
				downloadDocumentWithDocumentHelper,
				raiseDochelperError
			},
			actionTag: isDisabled ? actionTags.openReadOnly : actionTag,
			labels: {
				documentHelperConnectionIssue: labels.documentHelperConnectionIssue
			}
		};

		daasDocumentCheckoutComplete || daasDocumentCheckinError
			? openDaaSDocument(contentUri)
			: Utility.openDocumentForEditing(payload);
	};

	const onHideRelateTaskModal = () => {
		setShowRelateTaskModal(false);
		getRelatedDocuments && getRelatedDocuments();
	};

	const getEntitiesRelatedToDocument = useCallback((page, pageSize, searchText, entities) => {
		return dispatch(
			getDocumentByDocumentId(
				engagementId,
				documentId,
				{
					includeRelatedEntities: true
				},
				true
			)
		).then((response) => {
			const docEntities = response?.entities?.map((entity) => entity.id) || [];
			let allAvailableEntities = entities?.data;
			let allDocEntities = [];
			if (searchText.length > 0) {
				allAvailableEntities = allAvailableEntities.filter(
					(entity) =>
						entity.stEntityShortName.toLowerCase().includes(searchText.toLowerCase()) ||
						entity.stEntityName.toLowerCase().includes(searchText.toLowerCase())
				);
				allDocEntities = allAvailableEntities
					.filter((entity) => docEntities.includes(entity.id))
					.sort(Utility.sortByMultipleKey('stEntityName'));
			} else {
				allDocEntities = allAvailableEntities
					.filter((entity) => docEntities.includes(entity.id))
					.sort(Utility.sortByMultipleKey('stEntityName'));
			}
			const totalEntityCount = allDocEntities.length;
			const pages = Math.ceil(totalEntityCount / pageSize);
			const startIndex = (page - 1) * pageSize;
			const endIndex = startIndex + pageSize;
			const pagedEntities = allDocEntities.slice(startIndex, endIndex);

			return {
				data: pagedEntities,
				totalEntityCount: totalEntityCount,
				page: page,
				pages: pages
			};
		});
	}, []);

	const onHideRelateEntitiesModal = () => {
		setShowRelateEntitiesModal(false);
		getRelatedDocuments && getRelatedDocuments();
	};

	const getFrameUrl = (url) => {
		let frameUrl;
		const stEntity = accountSTEntityId > 0 ? accountSTEntityId : aresUrlParameters?.stEntityId;
		if (stEntity) {
			frameUrl = `${url}&stentityid=${stEntity}&isdrawer=true`;
		} else {
			frameUrl = `${url}&isdrawer=true`;
		}
		return frameUrl;
	};

	const documentChekOutCheckInProcess = (actioType, processingLabel) => {
		setIsInProgress(true);
		dispatch(daasCheckInCheckOutDocument(engagementId, entityUId, documentId, parentName, actioType))
			.then(() => {
				dispatch(setFormV2ToasterVisibility(true, ToastMessageVariants.Success, processingLabel, '', 10000));
				window.dispatchEvent(new CustomEvent(clientSideEventNames.checkoutCheckin));
			})
			.finally(() => {
				setIsInProgress(false);
			});
	};

	const getSubString = (str, startStr, endStr) => {
		const startSeparatorLength = startStr.length;
		return str.substring(str.indexOf(startStr) + startSeparatorLength, str.lastIndexOf(endStr));
	};

	const getBodyLabelForCheckInAction = () => {
		return (
			<span>
				{labels.documentEndMultiUserEditingBody.split('{0}')[0]}
				{<b>{`'${labels.confirm}'`}</b>}
				{getSubString(labels.documentEndMultiUserEditingBody, '{0}', '{1}')}
				{<b>{`'${labels.cancelLabel}'`}</b>}
				{labels.documentEndMultiUserEditingBody.split('{1}')[1]}
			</span>
		);
	};

	const getHeaderLabelForCheckOutAction = () => {
		const documentEditUserCreateDateLocalFormat = Moment.utc(documentEditUserCreateDate)
			.local()
			.format(dateTimeFormat);

		return (
			<span>
				<b>
					{`${labels.documentCurrentlyBeingModifiedHeader.replace('{0}', documentEditUserName)} `}
					<ReactMoment fromNow locale={userPreferences?.displayLanguage}>
						{documentEditUserCreateDateLocalFormat}
					</ReactMoment>
					{'.'}
				</b>
			</span>
		);
	};

	const getBodyLabelForCheckOutAction = () => {
		let documentCurrentlyBeingModifiedBody = labels.documentCurrentlyBeingModifiedBody.replace(
			'{0}',
			documentEditUserName
		);
		return (
			<span>
				{documentCurrentlyBeingModifiedBody.split('{1}')[0]}
				{
					<span>
						<b>{`'${labels.startMultiuserEditing}'`}</b>
					</span>
				}
				{getSubString(documentCurrentlyBeingModifiedBody, '{1}', '{2}')}
				{
					<span>
						<b>{`'${labels.cancelLabel}'`}</b>
					</span>
				}
				{documentCurrentlyBeingModifiedBody.split('{2}')[1]}
			</span>
		);
	};

	const getDaasLabels = () => {
		if (daasAction.checkIn) {
			return {
				modalTitle: labels.documentEndMultiUserEditingTitle,
				modalHeader: labels.documentEndMultiUserEditingHeader,
				modalBody: getBodyLabelForCheckInAction(),
				confirmBtnTitle: labels.confirm,
				closeBtnTitle: labels.cancelLabel,
				closeTitle: labels.cancelLabel
			};
		}
		if (daasAction.checkOut) {
			return {
				modalTitle: labels.documentCurrentlyBeingModifiedTitle,
				modalHeader: getHeaderLabelForCheckOutAction(),
				modalBody: getBodyLabelForCheckOutAction(),
				confirmBtnTitle: labels.startMultiuserEditing,
				closeBtnTitle: labels.cancelLabel,
				closeTitle: labels.cancelLabel
			};
		}
	};

	const handleCheckOutCheckInDocumentFromModal = () => {
		if (daasAction.checkOut) {
			handleCheckOutDocument();
		} else if (daasAction.checkIn) {
			handleCheckInDocument();
		}
	};

	const handleCheckOutDocument = () => {
		documentChekOutCheckInProcess(daasOptions.checkout, labels.checkOutInProgressMessage);
	};

	const handleCheckInDocument = () => {
		documentChekOutCheckInProcess(daasOptions.checkin, labels.checkInInProgressMessage);
	};

	const openDaaSDocument = (contentUri) => {
		dispatch(getRelatedDocument(engagementId, documentId, entityUId)).then((result) => {
			if (result && result.data && result.data.length === 1) {
				let document = result.data[0];
				if (Utility.isDaaSDocumentCheckOutComplete(document) || Utility.isDaaSDocumentCheckInError(document)) {
					window.open(contentUri, '_blank');
				} else {
					dispatch(setFormV2ToasterVisibility(true, ToastMessageVariants.Error, labels.daasOpenDocumentWarning, '', 0));
				}
			}
		});
	};

	const startMultiuserEditing = () => {
		dispatch(getRelatedDocument(engagementId, documentId, entityUId)).then((result) => {
			if (result && result.data && result.data.length === 1) {
				let document = result.data[0];
				if (document.documentEditUserName) {
					setDaasAction({checkOut: true, checkIn: false});
					setShowModal(true);
					return;
				}

				if (Utility.isDaaSDocumentCheckOutInProgress(document) || Utility.isDaaSDocumentCheckOutComplete(document)) {
					dispatch(
						setFormV2ToasterVisibility(true, toastVariant.error, labels.daasStartCollaborationModeWarning, '', 0)
					);
				} else {
					handleCheckOutDocument();
				}
			}
		});
	};

	useEffect(() => {
		if (errors && errors.message) {
			setIsInProgress(false);
		}
	}, [errors]);

	let showAddDocumentIcon = showPriorPeriodMenuOptions || showExternalDocumentMenuOptions;

	if (groupInstructionDrawer && documentTypeId === documentTypes.PRIORYEAREVIDENCE) {
		showAddDocumentIcon = false;
	}

	return (
		<>
			{showDeleteModal && (
				<DeleteDocument
					engagementId={engagementId}
					document={documentObject}
					showDeleteModal={showDeleteModal}
					setShowDeleteModal={setShowDeleteModal}
					onDocumentDeleted={onDocumentDeleted}
					viewKey={viewKey}
					entityUId={entityUId}
					parentName={parentName}
					sectionId={sectionId}
					deleteDocumentAction={deleteDocumentAction}
					instructionId={instructionId}
				/>
			)}
			{showRenameModal && (
				<RenameDocument
					engagementId={engagementId}
					document={documentObject}
					showRenameModal={showRenameModal}
					setShowRenameModal={setShowRenameModal}
					onDocumentRenamed={onDocumentRenamed}
					viewKey={viewKey}
					entityUId={entityUId}
					userPreferences={userPreferences}
					isAresView={isAresView}
				/>
			)}
			{showRelateTaskModal && <RelateTaskContainer documentId={documentObject?.id} onHide={onHideRelateTaskModal} />}
			{showRelateEntitiesModal && (
				<RelateEntities
					showModal={showRelateEntitiesModal}
					parentName={parentName}
					engagementId={engagement.engagementId}
					document={documentObject}
					setShowModal={setShowRelateEntitiesModal}
					onHide={onHideRelateEntitiesModal}
					entityUId={entityUId}
					getEntitiesRelatedToDocument={getEntitiesRelatedToDocument}
					allEntities={allEntities}
				/>
			)}
			{showConfidentialityModal && (
				<ConfidentialityModal
					onConfidentialityChange={(payload) =>
						actions.updateDocument(
							engagement.engagementId,
							documentObject.id,
							payload,
							undefined,
							undefined,
							undefined,
							entityUId,
							viewKey
						)
					}
					parentContainerClassName={isAresView ? 'AresModal' : 'KronosModal'}
					setShowConfidentialityModal={setShowConfidentialityModal}
					labels={{
						confidentialityTitle: labels.confidentialityTitle,
						confidentialityText: labels.confidentialityText,
						confidentiality: labels.confidentiality,
						saveLabel: labels.saveLabel,
						cancelLabel: labels.cancelLabel,
						closeLabel: labels.closeLabel,
						placeholder: labels.confidentialityPlaceholder
					}}
					confidentialityLevels={currentResource.confidentialityLevels}
					userConfidentialityTypeId={userPreferences.userConfidentialityTypeId}
					documentConfidentialityTypeId={documentObject.confidentialityTypeId}
					theme={Utility.getTheme()}
				/>
			)}
			{showSignOffRequirementsModal && (
				<SignoffRequirementsModal
					parentContainerClassName={isAresView ? 'AresModal' : 'KronosModal'}
					document={documentObject}
					documentSize="medium"
					labels={{
						signoffRequirementsTitle: labels.signoffRequirementsModalTitle,
						modalDescription1: labels.signoffRequirementsModalDescription1,
						modalDescription2: labels.signoffRequirementsModalDescription2,
						saveLabel: labels.signoffRequirementsModalSaveLabel,
						cancelLabel: labels.signoffRequirementsModalCancelLabel,
						closeLabel: labels.signoffRequirementsModalCloseLabel,
						picLabel: labels.signoffRequirementsModalPICLabel,
						eqrLabel: labels.signoffRequirementsModalEQRLabel
					}}
					theme={Utility.getTheme()}
					onSaveButtonClick={(payload) =>
						actions
							.updateDocument(
								engagement.engagementId,
								documentObject.id,
								payload,
								undefined,
								undefined,
								undefined,
								entityUId,
								viewKey
							)
							.then(() => {
								window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
							})
					}
					setSignOffRequirementsModal={setShowSignOffRequirementsModal}
					isEngagementEQRSignoffRequired={engagement.isEQRSignoffRequired}
				/>
			)}

			{showHistoryModal && <>{/*Todo: History*/}</>}
			{engagement && (
				<>
					{documentNameControls && <section className="additionalControlIcon">{documentNameControls()}</section>}
					<DocumentName
						parentContainerClassName={isAresView ? 'AresModal' : 'KronosModal'}
						currentlyBeingModifiedByLabel={currentlyBeingModifiedByLabel}
						canvasFormOpenType={canvasFormOpenType || CanvasFormOpenType.Drawer}
						onOpenDocument={(url) => {
							if (isModalOpen) {
								window.open(url, '_blank');
								return;
							}
							if (
								Utility.isClassicSummaryScreenDocumentType(documentObject.documentTypeId) ||
								daasDocumentCheckoutComplete ||
								daasDocumentCheckinError
							) {
								Utility.isClassicSummaryScreenDocumentType(documentObject.documentTypeId)
									? window.open(url, '_blank')
									: openDaaSDocument(contentUri);
								return;
							}
							if (window.self === window.top) {
								toggleFrameModal({
									isAresView: true,
									variant: 'slideout',
									frameArgs: {
										name: documentObject.documentName,
										src: Utility.isDocumentCanvasForm(documentObject.documentTypeId) ? getFrameUrl(url) : url,
										allow: permissionsPolicy
									}
								});
							} else {
								frameService.openFrameFromChild({
									isAresView: true,
									variant: 'slideout',
									frameArgs: {
										name: documentObject.documentName,
										src: Utility.isDocumentCanvasForm(documentObject.documentTypeId) ? getFrameUrl(url) : url,
										allow: permissionsPolicy
									}
								});
							}
						}}
						dateFormat={dateFormat}
						document={{...documentObject, contentContractId}}
						documentEditUserCreateDate={documentEditUserCreateDate}
						documentEditUserName={documentEditUserName}
						ellipse={ellipse}
						ellipseMaxLines={ellipseMaxLines}
						enableOpenDocument={enableOpenDocument}
						confidentialityConfiguration={confidentialityConfiguration}
						userConfidentialityTypeId={userPreferences.userConfidentialityTypeId}
						confidentialityNotOpenableTooltipPlacement={confidentialityNotOpenableTooltipPlacement}
						confidentialityNotOpenableTooltipContentClassName={confidentialityNotOpenableTooltipContentClassName}
						showConfidential={showConfidential}
						engagement={engagement}
						isLink={isLink}
						forwardUrl={forwardUrl}
						isLoading={isLoading}
						lastModifiedByLabel={lastModifiedByLabel}
						fileSizeLabel={fileSizeLabel}
						lastModifiedFullName={lastModifiedFullName}
						modifiedDate={modifiedDate}
						openLabelText={openedLabelText}
						referrer={referrer}
						shareStatusOwnedLabel={shareStatusOwnedLabel}
						shareStatusSharedLabel={shareStatusSharedLabel}
						unlinkInProgressLabel={unlinkInProgressLabel}
						unlinkErrorLabel={unlinkErrorLabel}
						showCurrentUserInfo={showCurrentUserInfo}
						showShared={showShared}
						showLastModifiedUserInfo={showLastModifiedUserInfo}
						size={size}
						children={() => {
							return <></>;
						}}
						isReadOnly={isReadOnly}
						displayLanguage={userPreferences.displayLanguage}
						numberFormat={userPreferences.numberFormat}
						inProgress={inProgress || isInProgress}
						progressPercent={progressPercent ? progressPercent : 0}
						progressLabel={progressLabel}
						labelComponent={labels.processing}
						menuOptions={showMenuOptions && !(inProgress || isInProgress) ? menuOptionsMerged : undefined}
						useDocHelper={useDocHelper}
						loadDocHelper={{
							documentUserPresence: documentUserPresence,
							appOnlineState: appOnlineState,
							actions: actions,
							parentName: parentName,
							styleName: styleName,
							actionTag: actionTag,
							onDocumentClick: onDocumentClick,
							onGetDocumentUserPresence: onGetDocumentUserPresence,
							onDocumentUserPresenceChanged: onDocumentUserPresenceChanged,
							openDocument: openDocument,
							actionTags: actionTags,
							labels: {
								documentOpenModelLabel: labels.documentOpenModelLabel,
								closeLabel: labels.closeLabel,
								modelDocumentOpenedText: labels.modelDocumentOpenedText,
								modelUserOpenedTheDocumentText: labels.modelUserOpenedTheDocumentText,
								modelOpenedDocumentConflictText: labels.modelOpenedDocumentConflictText,
								clickHereEnabledText: labels.clickHereEnabledText,
								openReadOnly: labels.openReadOnly,
								open: labels.open,
								cancelLabel: labels.cancelLabel
							},
							dateTimeFormat: dateTimeFormat,
							entityEnum: Entity,
							numberFormat: userPreferences.numberFormat
						}}
						theme={Utility.getTheme()}
						renameDocumentErrors={renameDocumentErrors}
						updateDocumentOptionalArguments={[entityUId, viewKey]}
						roleLabels={currentResource.roleForMember}
						documentSignoffRequiredLabel={labels.documentSignoffRequiredLabel}
						addToEvidenceLabel={showTemporaryMenuOptions ? labels.moveToEvidenceLabel : labels.addToEvidenceLabel}
						showAddDocumentIcon={showAddDocumentIcon}
						onAddToEvidence={onAddToEvidence}
						disabled={disabled}
						target={documentTypeId === documentTypes.GUIDEDWORKFLOW ? '_self' : '_blank'}
						tooltipPosition={tooltipPosition}
						coeditModeLabels={{
							coeditMode: labels.coEditMode,
							coEditModeIsStarting: labels.coEditModeIsStarting,
							beingEditedInCoeditMode: labels.beingEditedInCoeditMode,
							beingEditedInCoeditModeOn: labels.beingEditedInCoeditModeOn,
							coEditModeIsEnding: labels.coEditModeIsEnding,
							beingEditedInCoeditModeError: labels.beingEditedInCoeditModeError,
							coEditModeAutomaticallyEnds: labels.coEditModeAutomaticallyEnds
						}}
						createDateDays={featureConfigurations.features?.returnFromCollaborationSitesCreatedDateDays}
						userPreferences={userPreferences}
						blockOnError={false}
					/>
				</>
			)}
			{showModal && (
				<ConfirmStartMultiuserEditing
					show={showModal}
					setShow={setShowModal}
					document={{
						documentName,
						fileExt,
						isShared,
						documentEditUserCreateDate,
						documentEditUserName,
						hasComments,
						hasHiddenSheets,
						isConflicted,
						trackChange,
						isReturned,
						isReadOnly
					}}
					onConfirmStartMultiuserEditing={() => {
						handleCheckOutCheckInDocumentFromModal();
						setShowModal(false);
					}}
					modalTitle={getDaasLabels().modalTitle}
					modalHeader={getDaasLabels().modalHeader}
					modalBody={getDaasLabels().modalBody}
					confirmBtnTitle={getDaasLabels().confirmBtnTitle}
					closeBtnTitle={getDaasLabels().closeBtnTitle}
					closeTitle={getDaasLabels().closeTitle}
				/>
			)}
		</>
	);
}
